/**
 * Security testing contracts (docs/design/security-testing.md): the Finding model, scan profiles, the
 * per-project authorization Scope that gates every scan, the risk summary shown to non-technical users,
 * and the SecurityPlan stored on a run.
 *
 * Engine-neutral: nothing here is Shannon-specific. The engine adapter (apps/runner/src/security/) maps a
 * tool's SARIF/JSON output into Finding[]; this file is the single source of truth the api, web and runner
 * all share. A scan runs ONLY against hosts a Project Admin has attested to in the project's Scope, never
 * production, so the module can be pointed only at applications the organization is authorized to test.
 */
import { z } from 'zod';
import { RUN_QUEUE_NAME } from './testing.js';
import { CODE_REPO_PROVIDERS } from './codeRepo.js';

// ---------- Severity ----------

/** Finding severity, highest first. */
export const SEVERITIES = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW', 'INFO'] as const;
export type Severity = (typeof SEVERITIES)[number];

export const SEVERITY_LABEL: Record<Severity, string> = {
  CRITICAL: 'Critical',
  HIGH: 'High',
  MEDIUM: 'Medium',
  LOW: 'Low',
  INFO: 'Informational',
};

/** Points one finding of each severity adds to the risk score (used for the exec grade, not a CVSS). */
export const SEVERITY_WEIGHT: Record<Severity, number> = { CRITICAL: 40, HIGH: 15, MEDIUM: 5, LOW: 1, INFO: 0 };

/**
 * How much a finding counts toward the grade by how far it was proven. An unproven POTENTIAL ("needs review")
 * candidate — all a recon-only Quick scan can produce — must not weigh the same as a proven exploit, or a
 * Quick scan grades worse than a Deep scan of the same app that verified and dismissed the same candidates.
 */
export const STATUS_WEIGHT: Record<FindingStatus, number> = { EXPLOITED: 1, CONFIRMED: 1, POTENTIAL: 0.3, INFO: 0 };

const severity = z.enum(SEVERITIES);

// ---------- Scan profiles ----------

/**
 * How hard a scan tries. QUICK is the one-click default: a fast baseline that probes but does not run
 * destructive exploits. DEEP is the full autonomous run (recon -> analysis -> real proof-of-concept
 * exploitation -> validation); it takes much longer and costs real AI usage, so the UI confirms it.
 */
export const SCAN_PROFILES = ['QUICK', 'DEEP'] as const;
export type ScanProfile = (typeof SCAN_PROFILES)[number];

/**
 * Which AI pentester runs the scan.
 * - SHANNON: KeygraphHQ/shannon — autonomous web/API pentester (recon -> analysis -> real exploit -> validate).
 * - CLAUDE_RED: an autonomous Claude agent that runs allowlisted offensive tools against the authorized target,
 *   guided by the SnailSploit/claude-red methodology skills. Requires an Anthropic model on the project/org.
 * Both run only against hosts the project's Scope has authorized (checkSecurityScope); never production unless opted in.
 */
// claude-red (black-box, from the public URL) is the default; Shannon is the source-aware engine (needs a repo).
export const SECURITY_ENGINES = ['CLAUDE_RED', 'SHANNON'] as const;
export type SecurityEngine = (typeof SECURITY_ENGINES)[number];

// SHANNON stays the stored value (old runs keep it); people only ever see the label.
export const SECURITY_ENGINE_LABEL: Record<SecurityEngine, string> = { SHANNON: 'Code-level security audit', CLAUDE_RED: 'Black-box pentest' };
export const SECURITY_ENGINE_HINT: Record<SecurityEngine, string> = {
  CLAUDE_RED:
    'Pentests the app from its public address like an outside attacker — no source code needed. Uses the project’s test data (e.g. a test login) to probe authenticated flows, then grades the app. Needs an Anthropic AI model.',
  SHANNON:
    'Audits the app’s source code together with the running app for deeper findings. Connect the GitHub / GitLab repository below; needs the project’s AI model. Deep scans run real proof-of-concept exploits.',
};

export const SCAN_PROFILE_LABEL: Record<ScanProfile, string> = { QUICK: 'Quick scan', DEEP: 'Deep scan' };
export const SCAN_PROFILE_HINT: Record<ScanProfile, string> = {
  QUICK: 'Fast baseline — probes for common issues without running destructive exploits. Minutes.',
  DEEP: 'Full autonomous pentest with real proof-of-concept exploitation and validation. Can take 1–1.5 hours and uses AI model credits.',
};

// ---------- Findings ----------

/**
 * How far a finding was proven. The engine keeps the tool's "no exploit, no report" discipline: EXPLOITED
 * findings carry a working proof of concept; POTENTIAL ones are candidates that still want human review.
 */
export const FINDING_STATUSES = ['EXPLOITED', 'CONFIRMED', 'POTENTIAL', 'INFO'] as const;
export type FindingStatus = (typeof FINDING_STATUSES)[number];

export const FINDING_STATUS_LABEL: Record<FindingStatus, string> = {
  EXPLOITED: 'Exploited',
  CONFIRMED: 'Confirmed',
  POTENTIAL: 'Needs review',
  INFO: 'Informational',
};

/** Where a finding was observed (all optional: some findings are code- or config-level, not request-bound). */
export const findingLocationSchema = z.object({
  url: z.string().max(8000).optional(),
  method: z.string().max(10).optional(),
  parameter: z.string().max(400).optional(),
  /** Source location when the engine tied it to the connected repo, e.g. "src/routes/user.ts:42". */
  file: z.string().max(1000).optional(),
});
export type FindingLocation = z.infer<typeof findingLocationSchema>;

/**
 * One security finding. `description`, `evidence` and `remediation` are plain prose the report shows; secret
 * values the engine captured are masked before they ever reach here (see the engine adapter).
 */
export const findingSchema = z.object({
  /** Stable id within the scan (the engine's rule/instance id), for dedup and comment anchoring. */
  id: z.string().min(1).max(200),
  title: z.string().min(1).max(300),
  severity,
  status: z.enum(FINDING_STATUSES),
  /** OWASP-style category, e.g. "A03:2021 Injection" or "Broken Access Control". */
  category: z.string().max(200),
  /** CWE id when known, e.g. "CWE-89"; null otherwise. */
  cwe: z.string().max(40).nullable(),
  /** What it is and why it matters, in plain language (safe to show a non-technical reader). */
  description: z.string().max(20000),
  /** The proof: what the engine did and what came back. Present for EXPLOITED/CONFIRMED findings. */
  evidence: z.string().max(40000).optional(),
  /** How to fix it. */
  remediation: z.string().max(20000),
  location: findingLocationSchema.optional(),
  /** 0–1: how sure the engine is. The report shows it as Low/Medium/High. */
  confidence: z.number().min(0).max(1),
  references: z.array(z.string().max(2000)).max(20).default([]),
});
export type Finding = z.infer<typeof findingSchema>;

export const findingsSchema = z.array(findingSchema).max(2000);

export const confidenceLabel = (c: number): 'Low' | 'Medium' | 'High' => (c >= 0.8 ? 'High' : c >= 0.5 ? 'Medium' : 'Low');

// ---------- Authorization scope ----------

const hostPattern = z
  .string()
  .min(1)
  .max(253)
  .regex(/^[A-Za-z0-9*]([A-Za-z0-9*-]*[A-Za-z0-9*])?(\.[A-Za-z0-9*]([A-Za-z0-9*-]*[A-Za-z0-9*])?)*$/, 'a hostname, optionally with a leading *. wildcard');

/** Who attested, and when, that the organization is authorized to run scans against the scope's hosts. */
export const attestationSchema = z.object({
  byUserId: z.string().min(1),
  byName: z.string().min(1).max(200),
  at: z.string().datetime(),
});
export type Attestation = z.infer<typeof attestationSchema>;

/**
 * A project's security Scope: the only hosts scans may touch, plus the standing authorization. Stored on the
 * project (TestProject.securityScope). A scan is refused unless its target host matches an authorized host and
 * an attestation exists. Production is blocked unless a Project Admin has deliberately allowed it.
 */
export const securityScopeSchema = z.object({
  /** Host allowlist; a leading "*." matches sub-domains. Staging/test hosts, per the project. */
  authorizedHosts: z.array(hostPattern).max(50).default([]),
  /** Off by default: hosts that look like production are refused even if listed, unless this is on. */
  allowProduction: z.boolean().default(false),
  /** Free-text rules of engagement passed to the engine (e.g. "do not touch /admin, no email sends"). */
  rulesOfEngagement: z.string().max(10000).default(''),
  /** Null until a Project Admin attests authorization; required before any scan can run. */
  attestation: attestationSchema.nullable().default(null),
});
export type SecurityScope = z.infer<typeof securityScopeSchema>;

export const EMPTY_SCOPE: SecurityScope = { authorizedHosts: [], allowProduction: false, rulesOfEngagement: '', attestation: null };

/** The host of a URL, lower-cased, or null if it will not parse. */
export function hostOf(url: string): string | null {
  try {
    return new URL(url).hostname.toLowerCase();
  } catch {
    return null;
  }
}

/**
 * Multi-label public suffixes where the registrable domain is the last THREE labels (e.g. foo.co.uk), so we do
 * not treat two unrelated `*.co.uk` sites as the same site. Not exhaustive — the common ones; anything else
 * falls back to the last two labels.
 */
const MULTI_LABEL_SUFFIXES = new Set([
  'co.uk', 'org.uk', 'gov.uk', 'ac.uk', 'me.uk', 'net.uk',
  'co.in', 'net.in', 'org.in', 'firm.in', 'gen.in', 'ind.in',
  'com.au', 'net.au', 'org.au', 'co.jp', 'co.nz', 'co.za', 'co.kr',
  'com.br', 'com.sg', 'com.mx', 'com.tr', 'com.cn', 'com.hk',
]);

/**
 * The registrable ("same-site") domain of a host: the part an owner registers, e.g. `poivaa.in` for
 * `staging.api.poivaa.in`, or `foo.co.uk` for `a.b.foo.co.uk`. Heuristic (no full public-suffix list).
 */
export function registrableDomain(host: string): string {
  const labels = host.toLowerCase().replace(/\.$/, '').split('.').filter(Boolean);
  if (labels.length <= 2) return labels.join('.');
  const lastTwo = labels.slice(-2).join('.');
  return MULTI_LABEL_SUFFIXES.has(lastTwo) ? labels.slice(-3).join('.') : lastTwo;
}

/** Are two hosts on the same registrable domain (e.g. the app and its API on sibling sub-domains)? */
export function sameSite(a: string, b: string): boolean {
  const ra = registrableDomain(a);
  return ra.length > 0 && ra.includes('.') && ra === registrableDomain(b);
}

/** Does `host` match an allowlist entry (exact, or a "*." wildcard covering it and its sub-domains)? */
export function hostMatches(host: string, pattern: string): boolean {
  const h = host.toLowerCase();
  const p = pattern.toLowerCase();
  if (p.startsWith('*.')) {
    const base = p.slice(2);
    return h === base || h.endsWith(`.${base}`);
  }
  return h === p;
}

/**
 * Conservative "is this production?" heuristic. We would rather refuse a borderline host and make someone opt
 * in than scan production by accident. A host counts as non-production when it clearly carries a test marker.
 */
export function looksLikeProduction(host: string): boolean {
  const h = host.toLowerCase();
  if (h === 'localhost' || h.endsWith('.localhost') || h.endsWith('.local') || h.endsWith('.test') || h.endsWith('.internal')) return false;
  if (/(^|[.-])(staging|stage|dev|test|qa|uat|preprod|sandbox|demo|local)([.-]|$)/.test(h)) return false;
  // Literal IPs and bare single-label hosts are treated as non-production (lab/dev); everything else: assume prod.
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(h) || !h.includes('.')) return false;
  return true;
}

export interface ScopeCheck {
  ok: boolean;
  /** Why a scan is refused (shown to the user); absent when ok. */
  reason?: string;
}

/** Whether a scan against `targetUrl` is allowed by `scope`. The api calls this before queueing any scan. */
export function checkSecurityScope(targetUrl: string, scope: SecurityScope): ScopeCheck {
  const host = hostOf(targetUrl);
  if (!host) return { ok: false, reason: 'The target is not a valid URL.' };
  if (!scope.attestation) {
    return { ok: false, reason: 'A Project Admin must confirm you are authorized to test this application before any scan can run.' };
  }
  if (!scope.authorizedHosts.some((p) => hostMatches(host, p))) {
    return { ok: false, reason: `${host} is not in this project's authorized hosts. Add it in Security scope first.` };
  }
  if (looksLikeProduction(host) && !scope.allowProduction) {
    return { ok: false, reason: `${host} looks like a production host. Scanning production is turned off for this project.` };
  }
  return { ok: true };
}

// ---------- Risk summary (exec view) ----------

/** A–F letter grade for the whole scan, for the non-technical summary. */
export type RiskGrade = 'A' | 'B' | 'C' | 'D' | 'F';

export interface RiskSummary {
  grade: RiskGrade;
  /** 0 (perfect) – 100 (worst), derived from weighted severity counts; shown as a gauge. */
  score: number;
  /** Count of findings per severity (INFO included). */
  counts: Record<Severity, number>;
  /** How many carry a working proof of concept. */
  exploited: number;
  total: number;
  /** One-line plain-English headline for the report header. */
  headline: string;
}

/** `proven` = CRITICAL/HIGH counts among EXPLOITED/CONFIRMED findings; only those force the F/D floor. */
function gradeFor(score: number, proven: { critical: number; high: number }): RiskGrade {
  if (proven.critical > 0 || score >= 60) return 'F';
  if (proven.high > 0 || score >= 30) return 'D';
  if (score >= 12) return 'C';
  if (score > 0) return 'B';
  return 'A';
}

/** Rolls findings up into the exec summary. Pure: the api and web both call it so the grade never disagrees. */
export function summarizeFindings(findings: Finding[]): RiskSummary {
  const counts: Record<Severity, number> = { CRITICAL: 0, HIGH: 0, MEDIUM: 0, LOW: 0, INFO: 0 };
  let exploited = 0;
  let raw = 0;
  const proven = { critical: 0, high: 0 };
  for (const f of findings) {
    counts[f.severity]++;
    if (f.status === 'EXPLOITED') exploited++;
    // Weight by severity AND how far it was proven: unproven candidates count, but far less than proven ones.
    raw += SEVERITY_WEIGHT[f.severity] * STATUS_WEIGHT[f.status];
    if (f.status === 'EXPLOITED' || f.status === 'CONFIRMED') {
      if (f.severity === 'CRITICAL') proven.critical++;
      else if (f.severity === 'HIGH') proven.high++;
    }
  }
  // Squash the unbounded weighted sum into 0–100 so one extra low finding never tips a clean app to "F".
  const score = Math.round(100 * (1 - Math.exp(-raw / 60)));
  const grade = gradeFor(score, proven);
  const actionable = counts.CRITICAL + counts.HIGH + counts.MEDIUM + counts.LOW;
  const headline =
    grade === 'A'
      ? 'No security issues were proven in this scan.'
      : counts.CRITICAL > 0
        ? `${counts.CRITICAL} critical issue${counts.CRITICAL > 1 ? 's' : ''} need urgent attention.`
        : `${actionable} issue${actionable > 1 ? 's' : ''} found${exploited ? `, ${exploited} with a working proof of concept` : ''}.`;
  return { grade, score, counts, exploited, total: findings.length, headline };
}

// ---------- Run plan + queue ----------

/** Snapshot of what a scan ran, stored on TestRun.securityPlan (like perfPlan); the report reads it back. */
export const securityPlanSchema = z.object({
  profile: z.enum(SCAN_PROFILES),
  /** Which AI pentester ran (defaults to Shannon for plans saved before engine selection existed). */
  engine: z.enum(SECURITY_ENGINES).default('SHANNON'),
  targetUrl: z.string().max(8000),
  /** True when the project had its repo connected, so the engine could do source-aware analysis. */
  repoConnected: z.boolean().default(false),
  /** Address of the connected code repository (ProjectCodeRepo). The code-level audit clones it; required by it. */
  repoUrl: z.string().max(2000).optional(),
  /** GITHUB / GITLAB / OTHER: picks the user name for the token when cloning. The token itself is never in the plan. */
  repoProvider: z.enum(CODE_REPO_PROVIDERS).optional(),
  /** Branch to audit; absent = the repository's default branch. */
  repoBranch: z.string().max(250).optional(),
  rulesOfEngagement: z.string().max(10000).default(''),
  /**
   * The project's attested authorized hosts when the scan was queued ("*." patterns allowed). The agent may also
   * test these hosts (e.g. the app's API on another sub-domain), not only the target. Empty on older plans = the
   * target host only.
   */
  authorizedHosts: z.array(z.string().max(253)).max(50).default([]),
  /** The scope's production switch when queued: hosts that look like production are refused unless it was on. */
  allowProduction: z.boolean().default(false),
});
export type SecurityPlan = z.infer<typeof securityPlanSchema>;

/** What the Run dialog sends to start a scan: the profile and which engine (the target comes from the project). */
export const securityRunInputSchema = z.object({
  profile: z.enum(SCAN_PROFILES).default('QUICK'),
  engine: z.enum(SECURITY_ENGINES).default('CLAUDE_RED'),
});
export type SecurityRunInput = z.infer<typeof securityRunInputSchema>;

/** Security scans run on their own runner platform/queue: they are heavy and long, so they never block web tests. */
export const SECURITY_QUEUE_NAME = `${RUN_QUEUE_NAME}-security`;
